import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { anbieterApi } from '../api';

const STATUS_FARBEN = { aktiv: '#22c55e', inaktiv: '#f59e0b', archiviert: '#6b7280', ungeprüft: '#f97316' };

export default function AnbieterPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await anbieterApi.list({ status: statusFilter || undefined, search: search || undefined, page, pageSize });
      setRows(r.rows || []); setTotal(r.total || 0);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleArchivieren = async (id) => {
    if (!window.confirm('Anbieter archivieren?')) return;
    try { await anbieterApi.archivieren(id); load(); } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleMigration = async () => {
    try {
      const r = await anbieterApi.migration();
      alert(`Migration: ${r.migrated} Anbieter aus Verträgen übernommen.`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>🏢 Anbieter</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={handleMigration} style={btnStyle('#64748b')}>Aus Verträgen übernehmen</button>
          <button onClick={() => navigate('/anbieter/neu')} style={btnStyle('#6366f1')}>+ Neuer Anbieter</button>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Name, Beschreibung…" style={inputStyle} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">Alle Status</option>
          <option value="aktiv">Aktiv</option>
          <option value="inaktiv">Inaktiv</option>
          <option value="ungeprüft">Ungeprüft</option>
          <option value="archiviert">Archiviert</option>
        </select>
      </div>

      {err && <div style={{ background: '#7f1d1d', padding: 12, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#6366f1', padding: 40 }}>⏳ Lade…</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
          <div style={{ fontSize: 40 }}>🏢</div>
          <div style={{ marginTop: 12 }}>Noch keine Anbieter vorhanden.</div>
          <div style={{ marginTop: 8, fontSize: 13, color: '#475569' }}>Tipp: „Aus Verträgen übernehmen" lädt bestehende Anbieterfelder automatisch.</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 8, color: '#94a3b8', fontSize: 13 }}>{total} Anbieter gesamt</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {rows.map(a => (
              <div key={a.id} onClick={() => navigate(`/anbieter/${a.id}`)}
                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{a.name}</span>
                    {a.kurzname && <span style={{ color: '#64748b', fontSize: 12 }}>({a.kurzname})</span>}
                    <span style={{ background: STATUS_FARBEN[a.status] || '#6b7280', color: '#fff', fontSize: 10,
                      padding: '2px 6px', borderRadius: 4 }}>{a.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, color: '#64748b', fontSize: 12, flexWrap: 'wrap' }}>
                    {a.kategorie && <span>📂 {a.kategorie}</span>}
                    {a.vertraege_anzahl > 0 && <span>📋 {a.vertraege_anzahl} Vertrag/Verträge</span>}
                    {a.kontakte_anzahl > 0 && <span>👤 {a.kontakte_anzahl} Kontakt/Kontakte</span>}
                    {a.kundendienst_telefon && <span>📞 {a.kundendienst_telefon}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => navigate(`/anbieter/${a.id}/bearbeiten`)} style={smallBtnStyle('#334155')}>Bearbeiten</button>
                  {a.archiviert === 0 && <button onClick={() => handleArchivieren(a.id)} style={smallBtnStyle('#7f1d1d')}>Archivieren</button>}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={btnStyle('#334155')}>‹ Zurück</button>
              <span style={{ padding: '6px 12px', color: '#94a3b8', fontSize: 13 }}>{page} / {Math.ceil(total/pageSize)}</span>
              <button onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(total/pageSize)} style={btnStyle('#334155')}>Weiter ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const btnStyle = (bg) => ({ background: bg, color: '#f1f5f9', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 });
const smallBtnStyle = (bg) => ({ background: bg, color: '#f1f5f9', border: 'none', padding: '5px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12 });
const inputStyle = { background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 12px', borderRadius: 6, fontSize: 13, minWidth: 200 };
const selectStyle = { background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 12px', borderRadius: 6, fontSize: 13 };
